import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Plus, Edit, Trash2, Save, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PinAuth from '../components/PinAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStories = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stories, setStories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    condition: '',
    story: '',
    imageUrl: ''
  });

  useEffect(() => {
    const savedPin = sessionStorage.getItem('adminPin');
    if (savedPin) {
      // Verify PIN with backend
      axios.get(`${API}/admin/pin/verify?pin=${savedPin}`)
        .then(response => {
          if (response.data.valid) {
            setIsAuthenticated(true);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('adminPin');
        });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadStories();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <PinAuth onSuccess={() => setIsAuthenticated(true)} title="Admin Stories" icon={BookOpen} />;
  }

  const loadStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error loading stories:', error);
      toast.error('Failed to load stories');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing story
        await axios.patch(`${API}/stories/${editingId}`, formData);
        toast.success('Story updated successfully');
      } else {
        // Create new story
        await axios.post(`${API}/stories`, formData);
        toast.success('Story created successfully');
      }
      
      resetForm();
      loadStories();
    } catch (error) {
      console.error('Error saving story:', error);
      toast.error('Failed to save story');
    }
  };

  const handleEdit = (story) => {
    setFormData({
      name: story.name,
      condition: story.condition,
      story: story.story,
      imageUrl: story.imageUrl || ''
    });
    setEditingId(story.id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    
    try {
      await axios.delete(`${API}/stories/${id}`);
      toast.success('Story deleted successfully');
      loadStories();
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Failed to delete story');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      condition: '',
      story: '',
      imageUrl: ''
    });
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <div className="admin-stories-page min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Manage Stories</h1>
          <p className="text-gray-600">Add, edit, or remove patient stories</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-1">
            <Card className="border-none shadow-xl sticky top-4">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isEditing ? 'Edit Story' : 'Add New Story'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Patient/Family Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="condition">Disease/Condition *</Label>
                    <Input
                      id="condition"
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      placeholder="Cancer, Stroke, etc."
                      required
                      className="border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="story">Story *</Label>
                    <Textarea
                      id="story"
                      name="story"
                      value={formData.story}
                      onChange={handleChange}
                      placeholder="Share the story of how Dard Satya helped..."
                      rows={8}
                      required
                      className="border-blue-200 focus:border-blue-500 resize-none"
                    />
                  </div>

                  <div>
                    <Label htmlFor="imageUrl">Image URL (optional)</Label>
                    <Input
                      id="imageUrl"
                      name="imageUrl"
                      value={formData.imageUrl}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save size={16} className="mr-2" />
                      {isEditing ? 'Update' : 'Create'}
                    </Button>
                    
                    {isEditing && (
                      <Button 
                        type="button"
                        onClick={resetForm}
                        variant="outline"
                        className="border-gray-300"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Stories List */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">All Stories ({stories.length})</h2>
            </div>

            {stories.length === 0 ? (
              <Card className="border-none shadow-lg">
                <CardContent className="p-12 text-center">
                  <Plus size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 text-lg">No stories yet</p>
                  <p className="text-gray-500 text-sm mt-2">Create your first story using the form</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {stories.map((story) => (
                  <Card key={story.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{story.name}</h3>
                          <p className="text-blue-600 font-semibold mb-3">{story.condition}</p>
                          <p className="text-gray-700 leading-relaxed mb-4 line-clamp-3">{story.story}</p>
                          
                          {story.imageUrl && (
                            <div className="mb-3">
                              <img 
                                src={story.imageUrl} 
                                alt={story.name}
                                className="w-32 h-32 object-cover rounded-lg"
                              />
                            </div>
                          )}
                          
                          <div className="flex gap-2 text-xs text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {new Date(story.timestamp).toLocaleDateString()}
                            </span>
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                              {story.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleEdit(story)}
                            variant="outline"
                            className="border-blue-600 text-blue-600 hover:bg-blue-50"
                            size="sm"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDelete(story.id)}
                            variant="outline"
                            className="border-red-600 text-red-600 hover:bg-red-50"
                            size="sm"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStories;
